import React, { useState } from 'react'
import styled from 'styled-components'
import { useIsClient } from 'usehooks-ts'
import { useIsDesktop } from 'utils/hooks'
import { getLocalStorage, setLocalStorage } from 'utils/storages'
import { BtnType, Button } from 'components/common/button'
import { Card } from 'components/common/card'
import { Column } from 'components/common/layout'
import { Modal } from 'components/common/modal'
import { Tag, TagType } from 'components/common/tag'
import { BoldText, DescText, SmallText } from 'components/common/text'
import { HIDE_HIRING_REPORT_NOTICE_KEY } from 'infra/constants'
import { theme } from 'infra/theme'
import { useStartupUser } from 'apis/hooks'

export const HiringReportNoticeModal: React.FC = () => {
  const { data: user } = useStartupUser()
  const [isOpen, setIsOpen] = useState(true)
  const isDesktop = useIsDesktop()
  const isClient = useIsClient()
  const isNew = user && user.createdAt > '2025-07-01T00:00:00Z' // 2025년 7월 1일 이후 가입자 대상
  const shouldNotShowAgain = getLocalStorage<boolean>( // 다시 보지 않기 했을 때
    HIDE_HIRING_REPORT_NOTICE_KEY,
    false,
  )
  const hasClosed = // X 버튼 눌렀을 때
    isClient && !!sessionStorage.getItem(HIDE_HIRING_REPORT_NOTICE_KEY)
  if (
    !user ||
    isNew ||
    !isDesktop ||
    !isOpen ||
    shouldNotShowAgain ||
    hasClosed
  )
    return null
  return (
    <Modal
      onClose={() => {
        sessionStorage.setItem(
          HIDE_HIRING_REPORT_NOTICE_KEY,
          JSON.stringify(true),
        )
        setIsOpen(false)
      }}
      width={600}
      disableBackgroundClick
    >
      <ModalCard>
        <ButtonText
          onClick={() => {
            setLocalStorage(HIDE_HIRING_REPORT_NOTICE_KEY, true)
            setIsOpen(false)
          }}
        >
          다시 보지 않기
        </ButtonText>
        <Column style={{ gap: 16 }}>
          <BoldText style={{ fontSize: theme.fontSize.h4 }}>
            🚨 “채용 집중 조사 기간”과 “면제 기간” 안내
          </BoldText>
          <DescText style={{ color: theme.color.black2 }}>
            {`그룹바이를 통해 채용한 사실을 2주 이내 알리지 않을 경우, 채용수수료율이 상향 조정되고 위약금이 발생합니다. 간혹 채용 사실 공유를 누락하여 위약금이 청구되는 일이 발생하고 있습니다.\n이에 아직 고지하지 않은 채용 사실을 알릴 시, 채용수수료율 상향 조정과 위약금 청구를 전부 면제해 드리는 “면제 기간”을 마련하여 안내 드립니다.`}
          </DescText>
        </Column>
        <Column>
          <CustomTag variant={TagType.BLUE}>
            채용수수료 상향 조정 및 위약금 면제 기간
          </CustomTag>
          <ListContainer>
            <li>
              면제 기간 : <b>2025.07.08 (화) ~ 2025.07.18 (금)</b>
            </li>
            <li>면제 범위 : 채용수수료율 상향 조정, 위약금 청구 면제</li>
            <li>
              공유 대상 채용 : 그룹바이를 통해 만난 후보자를 1년 이내 채용한
              경우
            </li>
            <li>공유 방법 (택 1)</li>
            <ListContainer>
              <li>
                {'아래 "미고지 채용 사실 알리기" 버튼을 클릭하여 폼 제출'}
              </li>
              <li>{'플랫폼에서 "최종합격" 상태로 변경'}</li>
              <li>
                <span style={{ textDecoration: 'underline' }}>
                  support@groupby.biz
                </span>{' '}
                이메일로 채용자 이름, 근로시작일 공유
              </li>
            </ListContainer>
          </ListContainer>
        </Column>
        <Column>
          <CustomTag variant={TagType.RED}>채용 집중 조사 기간</CustomTag>
          <ListContainer>
            <li>
              조사 기간 : <b>2025.07.21 (월) ~ 2025.08.29 (금)</b>
            </li>
            <li>
              조사 기간 내에 발견되는 채용 건은 계약서에 따라 위약금 청구가
              진행될 예정이니 면제 기간 내에 반드시 공유해주시길 바랍니다.
            </li>
          </ListContainer>
        </Column>
        <Button
          variant={BtnType.SECONDARY}
          style={{ width: 300, margin: '0 auto' }}
          onClick={() => window.open('https://tally.so/r/mJz12d', '_blank')}
        >
          미고지 채용 사실 알리기
        </Button>
      </ModalCard>
    </Modal>
  )
}

const ModalCard = styled(Card)`
  padding: 48px 32px 32px;
  gap: 24px;
`

const ButtonText = styled(SmallText)`
  position: absolute;
  right: 48px;
  top: 20px;
  color: ${(p) => p.theme.color.gray1};
  text-decoration: underline;
  cursor: pointer;
`

const CustomTag = styled(Tag)`
  font-weight: ${(p) => p.theme.fontWeight.bold};
  font-size: ${(p) => p.theme.fontSize.default};
  margin-bottom: 8px;
`

const ListContainer = styled.ul`
  padding-left: 24px;
  font-size: ${(p) => p.theme.fontSize.default};
  color: ${(p) => p.theme.color.black2};
  line-height: 1.6;
`
